import { type CatalogProduct } from "@/domain/catalog";
import {
	isConsultantOnly,
	isHostOnly,
	isPurchasable
} from "@/domain/catalog/online-status";
import { type AddToCartButtonState } from "./AddToCartButtonView";

export interface AddToCartStateInput {
	isConsultant?: boolean;
	loading?: boolean;
	error?: string | null;
	submitted?: boolean;
}

/**
 * Add-to-Cart button state. Who may buy it is asked first, then whether it can
 * be bought at all, then the request's own progress.
 *
 * The lifecycle question is a single call: search-sync has already reconciled
 * PIM, the CMS override and inventory into one status, so there is nothing left
 * here to re-derive.
 */
export function deriveAddToCartState(
	product: Pick<CatalogProduct, "onlineStatus" | "availableTo">,
	input: AddToCartStateInput = {}
): AddToCartButtonState {
	const {
		isConsultant = false,
		loading = false,
		error = null,
		submitted = false
	} = input;

	const availableTo = product.availableTo ?? [];

	if (isHostOnly(availableTo)) return { kind: "host-exclusive" };
	if (isConsultantOnly(availableTo) && !isConsultant) {
		return { kind: "consultant-only-gated" };
	}
	if (!isPurchasable(product.onlineStatus)) {
		return unpurchasableReason(product.onlineStatus);
	}
	if (loading) return { kind: "loading" };
	if (submitted) return { kind: "success" };
	if (error) return { kind: "error", message: error };
	return { kind: "idle" };
}

function unpurchasableReason(
	status: CatalogProduct["onlineStatus"]
): AddToCartButtonState {
	switch (status) {
		case "out-of-stock":
			return { kind: "out-of-stock" };
		case "temporarily-unavailable":
			return { kind: "temporarily-unavailable" };
		case "pre-launch":
			return { kind: "pre-launch" };
		default:
			return { kind: "discontinued" };
	}
}

export function isCartDisabledByState(
	kind: AddToCartButtonState["kind"]
): boolean {
	return (
		kind === "out-of-stock" ||
		kind === "discontinued" ||
		kind === "temporarily-unavailable" ||
		kind === "pre-launch" ||
		kind === "host-exclusive" ||
		kind === "consultant-only-gated"
	);
}
